import React, { useState, useEffect, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { FaArrowRight, FaTimes, FaCheckCircle } from "react-icons/fa";
import { Link } from "react-scroll";
const staticServices = [
  { _id: "1", title: "Web Development", tagline: "Modern & Fast", shortDesc: "Custom websites built with React.js & Node.js, optimized for speed and SEO.", fullDesc: "We build modern, fast, and scalable websites tailored to your business needs using the latest technologies like React.js, Next.js, and Node.js.", features: ["React.js Frontend", "Node.js Backend", "SEO Optimized", "Mobile Responsive", "Fast Loading", "Secure & Scalable"], color: "#f59e0b", image: "/images/web-dev.jpeg" },
  { _id: "2", title: "Mobile App Development", tagline: "Android & iOS", shortDesc: "Cross-platform Flutter apps for Android & iOS with Firebase backend.", fullDesc: "We develop high-quality cross-platform mobile applications using Flutter that work seamlessly on both Android and iOS devices.", features: ["Flutter Framework", "Android & iOS", "Firebase Backend", "Push Notifications", "Offline Support", "App Store Deployment"], color: "#d97706", image: "/images/mobile-app.jpeg" },
  { _id: "3", title: "Digital Marketing", tagline: "Grow Online", shortDesc: "SEO, Google Ads & Social Media campaigns to grow your business online.", fullDesc: "Comprehensive digital marketing solutions including SEO, Google Ads, Meta Ads, and social media management to boost your online presence.", features: ["SEO & SEM", "Google Ads", "Meta Ads", "Email Campaigns", "Analytics & Reports", "Content Strategy"], color: "#ef4444", image: "/images/digital-marketing.jpeg" },
  { _id: "4", title: "Business Strategy", tagline: "Plan & Scale", shortDesc: "Expert consulting to plan, grow & scale your business effectively.", fullDesc: "Strategic business consulting to help you plan, execute, and scale your business with data-driven insights and proven methodologies.", features: ["Business Planning", "Market Research", "Competitor Analysis", "Growth Roadmap", "Financial Planning", "Risk Assessment"], color: "#f59e0b", image: "/images/business-strategy.jpeg" },
  { _id: "5", title: "Domain & Hosting", tagline: "Go Live Fast", shortDesc: "Domain registration, hosting, SSL & business email setup.", fullDesc: "Complete web hosting solutions including domain registration, web hosting, SSL certificates, and professional business email setup.", features: ["Domain Registration", "Web Hosting", "SSL Certificate", "Business Email", "99.9% Uptime", "24/7 Support"], color: "#10b981", image: "/images/domain-hosting.jpeg" },
  { _id: "6", title: "Social Media Management", tagline: "Build Presence", shortDesc: "Complete social media handling — content creation, posting & growth.", fullDesc: "End-to-end social media management including content creation, scheduling, community management, and growth analytics.", features: ["Content Creation", "Daily Posting", "Ad Campaigns", "Growth Analytics", "Community Management", "Monthly Reports"], color: "#fbbf24", image: "/images/social media management.jpeg" },
];

function ServiceModal({ service, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto glass rounded-2xl sm:rounded-3xl border border-white/10 shadow-2xl"
        style={{ background: "rgba(10, 8, 5, 0.97)" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 sm:p-6 border-b border-white/10"
          style={{ background: "rgba(10, 8, 5, 0.97)" }}>
          <div className="flex items-center gap-3">
            {service.image && (
              <img src={service.image} alt={service.title}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl object-cover" />
            )}
            <div>
              <h2 className="text-white font-black text-lg sm:text-2xl">{service.title}</h2>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ background: `${service.color}20`, color: service.color }}>
                {service.tagline}
              </span>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 sm:w-10 sm:h-10 glass rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors flex-shrink-0">
            <FaTimes />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
          {/* Description */}
          <p className="text-gray-300 leading-relaxed text-sm sm:text-base">{service.fullDesc}</p>

          {/* Features */}
          {service.features?.length > 0 && (
            <div>
              <h3 className="text-white font-bold text-base sm:text-lg mb-3 sm:mb-4">What's Included</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {service.features.map((f) => (
                  <div key={f} className="flex items-center gap-2 sm:gap-3">
                    <FaCheckCircle className="flex-shrink-0 text-sm" style={{ color: service.color }} />
                    <span className="text-gray-300 text-xs sm:text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}



          {/* CTA */}
          <div className="rounded-2xl p-4 sm:p-6 text-center"
            style={{ background: `${service.color}15`, border: `1px solid ${service.color}30` }}>
            <p className="text-white font-semibold text-sm sm:text-base mb-3">Have a custom requirement? Let's discuss!</p>
            <Link to="contact" smooth duration={600} offset={-80} onClick={onClose}>
              <button className="btn-primary px-6 sm:px-8 py-2.5 sm:py-3 rounded-full text-white font-semibold text-sm">
                <span>Get Free Consultation</span>
              </button>
            </Link>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ServiceCard({ service, index, onLearnMore }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="relative glass rounded-2xl card-hover group cursor-pointer overflow-hidden flex flex-col"
    >
      {/* Card Image */}
      <div className="relative h-44 overflow-hidden">
        {service.image ? (
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
        ) : null}
        <div
          className={`${service.image ? "hidden" : "flex"} w-full h-full items-center justify-center`}
          style={{ background: `linear-gradient(135deg, ${service.color}20, ${service.color}05)` }}
        >
          <span className="text-5xl font-black" style={{ color: service.color }}>
            {service.title?.[0]}
          </span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1e] via-black/20 to-transparent" />
      </div>

      <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl`}
        style={{ background: `linear-gradient(135deg, ${service.color}10, ${service.color}05)` }} />

      <div className="relative flex flex-col flex-1 p-5 sm:p-6">
        <h3 className="text-lg sm:text-xl font-bold text-white mb-1">{service.title}</h3>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full w-fit mb-3"
          style={{ background: `${service.color}20`, color: service.color, border: `1px solid ${service.color}35` }}>
          {service.tagline}
        </span>
        <p className="text-gray-400 leading-relaxed text-sm flex-1">{service.shortDesc}</p>
        <button
          onClick={() => onLearnMore(service)}
          className="mt-5 flex items-center gap-2 text-sm font-semibold transition-all group-hover:gap-4 w-fit"
          style={{ color: service.color }}
        >
          Learn More <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </motion.div>
  );
}

export default function Services() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => { setServices(staticServices); }, []);

  return (
    <section id="services" className="py-16 sm:py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }} className="text-center mb-12 sm:mb-16">
          <span className="text-yellow-400 font-semibold text-xs sm:text-sm uppercase tracking-widest">What We Offer</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mt-3 mb-4">
            Our <span className="gradient-text">Services</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">
            End-to-end technology & business solutions tailored to your needs. Click on any service to learn more.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {services.slice(0, 3).map((service, i) => (
            <ServiceCard key={service._id} service={service} index={i} onLearnMore={setSelectedService} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedService && (
          <ServiceModal service={selectedService} onClose={() => setSelectedService(null)} />
        )}
      </AnimatePresence>
    </section>
  );
}
